// src/components/common/ProtectedRoute.jsx
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { logout } from '../../store/slices/authSlice';
import { toast } from 'react-toastify';

const ProtectedRoute = ({ 
  children, 
  allowedRoles = ['driver', 'admin'],
  requireAdmin = false,
  requireDriver = false
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp < currentTime) {
          console.warn('⚠️ Token expiré, déconnexion...');
          toast.error('Votre session a expiré. Veuillez vous reconnecter.');
          dispatch(logout());
        }
      } catch (error) {
        console.error('❌ Token invalide:', error);
        toast.error('Session invalide. Veuillez vous reconnecter.');
        dispatch(logout());
      }
    }
  }, [dispatch, location]);

  if (!isAuthenticated) {
    toast.info('Veuillez vous connecter pour continuer');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user) {
    // Les admins ont accès à tout (sauf si requireDriver est true)
    const isAdmin = user.role === 'admin';
    const isDriver = user.role === 'driver';
    
    // Si requireDriver est true, seuls les drivers ont accès (pas les admins)
    if (requireDriver && !isDriver) {
      toast.error('Accès réservé aux livreurs uniquement.');
      return <Navigate to="/login" replace />;
    }
    
    // Si requireAdmin est true, seuls les admins ont accès
    if (requireAdmin && !isAdmin) {
      toast.error('Accès réservé aux administrateurs uniquement.');
      return <Navigate to="/login" replace />;
    }
    
    // Vérification standard des rôles (admin a accès par défaut)
    if (allowedRoles.length > 0) {
      const hasAllowedRole = allowedRoles.includes(user.role) || isAdmin;
      
      if (!hasAllowedRole) {
        console.error(`❌ Accès refusé. Rôle requis: ${allowedRoles.join(', ')}, Rôle actuel: ${user.role}`);
        toast.error(`Accès refusé. Rôles autorisés : ${allowedRoles.join(', ')}.`);
        return <Navigate to="/login" replace />;
      }
    }
  }

  return children;
};

export default ProtectedRoute;